import { CancelTokenSource } from 'axios';

import { LineItem, Package, Plan, QueryParams } from '@/common/models';
import { getStore } from '@/globals';
import OrgAppAPIProxy from '@/org-app/api';
import { useMediaOwners, useNetworks } from '@/org-app/services/tanstack/queries';
import { runQueryInScope } from '@/org-app/services/tanstack/queryUtils';

export enum MapDataAccessStatus {
  Public = 'Public',
  Private = 'Private',
}

interface ClusteredArgs {
  params: never;
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
  zoom: number;
  cancelTokeSource: CancelTokenSource;
  showLoader: boolean;
  type: string;
  isPrivate: boolean;
}

/**
 * MapDataProvider
 * This class a helper that will choose the right API to call depending on if your plan is private or public.
 * If no a plan, it'll call the regular API
 */
export default class MapDataProvider {
  private entity: Plan | LineItem | Package;

  private store: any;

  private accessStatus: MapDataAccessStatus;

  constructor(entity: Plan | LineItem | Package = null) {
    this.entity = entity;
    this.store = getStore();
    this.accessStatus =
      window?.location?.pathname?.includes('proposal') || window?.location?.pathname?.includes('monitoring')
        ? MapDataAccessStatus.Public
        : MapDataAccessStatus.Private;
  }

  async getClusteredUnitsFromTargets(args: ClusteredArgs): Promise<any> {
    return this.dispatch('getClusteredUnitsFromTargets', args);
  }

  async getClusteredSiteNetworksFromTargets(args: ClusteredArgs): Promise<any> {
    return this.dispatch('getClusteredSiteNetworksFromTargets', args);
  }

  getUnits(args: QueryParams): Promise<any> {
    // public plan has limited access to the API. use expands to get the information we need
    if (this.isPublicPlan && args?.expand?.length >= 0) {
      args.expand.push('network');
      args.expand.push('unit_languages.language');
    }

    return this.dispatch(this.isPublicPlan ? 'getUnitsForPublicPlan' : 'getUnits', {
      queryParams: args,
      cancelTokenSource: args.cancelTokenSource,
      showLoader: args.showLoader,
    });
  }

  getSites(args: QueryParams) {
    return this.dispatch(this.isPublicPlan ? 'getSitesForPublicPlan' : 'getSites', {
      queryParams: args,
      cancelTokenSource: args.cancelTokenSource,
      showLoader: args.showLoader,
    });
  }

  getEstimatedDeliveryStatistics({ params, cancelTokenSource }) {
    return this.dispatch('getEstimatedDeliveryStatistics', { params, cancelTokenSource });
  }

  getUnitsHourlyImpressions(params: any) {
    return this.dispatch('getUnitsHourlyImpressions', params);
  }

  async getMediaOwners() {
    if (this.isPublicPlan) {
      return OrgAppAPIProxy.instance.getMediaOwners({ planUuid: this.getPlanUuid() });
    }
    return runQueryInScope(useMediaOwners);
  }

  getNetworks(args: QueryParams) {
    if (this.isPublicPlan)
      return this.dispatch('getNetworksForPublicPlan', {
        queryParams: args,
        cancelTokenSource: args.cancelTokenSource,
        showLoader: false,
      });

    return runQueryInScope(useNetworks);
  }

  get isPlan(): boolean {
    return this.entity instanceof Plan;
  }

  get isPublicPlan(): boolean {
    return this.isPlan && this.accessStatus === MapDataAccessStatus.Public;
  }

  get hasChosenOrganization(): boolean {
    return this.store.getters.hasChosenOrganization;
  }

  get hasChosenExchange(): boolean {
    return this.store.hasChosenExchange;
  }

  get isCuratorOrganization(): boolean {
    return this.store.getters['orgApp/isCuratorOrganization'];
  }

  get orgExchDispatcher(): string {
    if (this.hasChosenOrganization && !this.isCuratorOrganization) return 'orgApp';
    if (this.hasChosenExchange || this.isCuratorOrganization) return 'exchApp';
    if (this.accessStatus === MapDataAccessStatus.Public) return 'orgApp';
    return null;
  }

  private dispatch(action: string, params: any): Promise<any> {
    // in private mode, you should be logged in and have an org type selected
    if (this.orgExchDispatcher === null && this.accessStatus === MapDataAccessStatus.Private) {
      throw new Error('Either choose organization or exchange');
    }

    const extraParams: { isPrivate: boolean; planUuid?: string } = {
      isPrivate: !this.isPublicPlan,
    };

    if (this.accessStatus === MapDataAccessStatus.Public) {
      extraParams.planUuid = this.getPlanUuid();
    }

    if (this.accessStatus === MapDataAccessStatus.Public && this.getPlanUuid() === null) {
      throw new Error('Missing planUUid');
    }

    return this.store.dispatch(`${this.orgExchDispatcher}/${action}`, { ...params, ...extraParams });
  }

  private getPlanUuid(): string {
    if (this.isPublicPlan && this.entity instanceof Plan) {
      return this.entity.uuid;
    }

    return null;
  }
}
